import { hiddenStarts, type DayEvent } from '../stores/events'
import type { WakeProgram } from './types'
import { sleepNormOf } from './programs'
import { totalMinutes } from './engine'
import { DEFAULT_DAY, NIGHT_END_MIN, dayStartOfEpochDay, earliestDayStart, isNight, nextDayStart, type DaySettings } from './day'
import { epochDayOf } from './days'

/**
 * Сводка дня для календаря: окна бодрствования, сон за сутки, ночные пробуждения.
 *
 * Правила заказчика (26–28.09.2026): перегулов здесь нет — они только в таймере. Сон
 * считается точно, если на «Уснул?» ответили «да» (от ответа до следующего «Проснулся»),
 * и примерно во всех остальных случаях — тогда сводка помечается `sleepApprox`.
 * Сутки идут от подъёма до подъёма: ночной сон относится к дню, вечером которого начался.
 *
 * Чистые функции: всё, что нужно, приходит параметрами, и всё проверяется тестами.
 */

export type Rating = 'ok' | 'low' | 'high' | 'none'

export interface DayStats {
  /** Окон бодрствования днём (ночные не считаются). */
  windows: number
  windowsNorm: readonly [number, number]
  windowsRating: Rating
  /** Сон за сутки, минуты, включая ночной. */
  sleepMinutes: number
  /** Норма сна за сутки, минуты (из программы, ВОЗ). */
  sleepNorm: readonly [number, number]
  sleepRating: Rating
  /** Хотя бы один промежуток сна посчитан без ответа «уснул». */
  sleepApprox: boolean
  nightWakes: number
}

const МИН = 60_000
const DAY = 24 * 3600 * 1000

/** Без ответа ночью считаем, что не спали полчаса: дольше кормление редко длится. */
const НОЧНОЕ_БЕЗ_ОТВЕТА_МИН = 30

/** Промежуток дольше этого — не сон, а брошенное приложение: в сумму не идёт (30 часов — решение заказчика 20.10.2026). */
const САМЫЙ_ДОЛГИЙ_СОН_МИН = 30 * 60

/** Где значение относительно нормы. */
export function rate(value: number, [min, max]: readonly [number, number]): Rating {
  if (value < min) return 'low'
  if (value > max) return 'high'
  return 'ok'
}

/**
 * Норма числа окон в день — из программы: сутки без нормы сна, делённые на окно по
 * возрасту, с допуском ±1 (решение 8 в `docs/sleep-weight-plan.md`).
 */
export function windowsNorm(program: WakeProgram): readonly [number, number] {
  const sleep = sleepNormOf(program)
  const awakeHours = 24 - (sleep.perDayHours[0] + sleep.perDayHours[1]) / 2
  const windowHours = (program.windowMin[0] + program.windowMin[1]) / 120
  const n = Math.round(awakeHours / windowHours)
  return [Math.max(1, n - 1), n + 1]
}

interface Пробуждение {
  at: number
  ночное: boolean
}

/**
 * Все пробуждения по порядку: начала окон из истории и ночные из журнала.
 *
 * Вид записи и решает, что это: в истории окон лежит нажатое «Проснулся», после которого
 * шли фазы, — это окно в любой час. Раньше здесь смотрели на часы, и раннее утро
 * оказывалось «ночным пробуждением», которого нет ни в одних сутках (найдено 02.10.2026).
 */
function пробуждения(events: DayEvent[], windows: number[]): Пробуждение[] {
  const все = new Map<number, Пробуждение>()
  // Начало, заменённое правкой времени, в истории больше не считается (20.10.2026)
  const скрытые = hiddenStarts(events)
  for (const at of windows) if (!скрытые.has(at)) все.set(at, { at, ночное: false })
  for (const e of events) {
    if (e.deleted === true) continue
    if (e.kind === 'night_wake') все.set(e.at, { at: e.at, ночное: true })
    // Окно, вписанное руками, в историю не попадает: нажатия не было, начало лежит в записи
    if (e.kind === 'window_end' && e.manual === true) все.set(e.window, { at: e.window, ночное: false })
  }
  return [...все.values()].sort((a, b) => a.at - b.at)
}

/**
 * Когда начинаются сутки календарного дня: с первого утреннего подъёма, если он случился
 * раньше начала дня (но не раньше пяти утра), иначе — с начала дня. Так ранний подъём
 * попадает в тот день, в который его и нажали.
 */
function началоСуток(day: number, все: Пробуждение[], settings: DaySettings): number {
  const обычное = dayStartOfEpochDay(day, settings)
  const порог = earliestDayStart(day, settings)
  const раннее = все.find((п) => !п.ночное && п.at >= порог && п.at < обычное)
  return раннее?.at ?? обычное
}

/**
 * Когда после пробуждения [п] уснули и точно ли это известно.
 *
 * Ответ «да, уснул» — точный момент. «Нет», сброс — момент ответа, но примерно: когда
 * уснули на самом деле, неизвестно. Без ответа вовсе — по плану окна.
 */
/** Запись о конце пробуждения [п]: самая ранняя из неудалённых — окно кончается один раз, поздний дубль растягивал бы сон (09.10.2026). */
function конецЗаписи(п: Пробуждение, events: DayEvent[]): DayEvent | undefined {
  return events
    .filter((e) => e.deleted !== true && e.window === п.at && (e.kind === (п.ночное ? 'night_wake_end' : 'window_end')))
    .sort((a, b) => a.at - b.at)[0]
}

function началоСна(п: Пробуждение, events: DayEvent[], program: WakeProgram): { at: number; точно: boolean } {
  const конец = конецЗаписи(п, events)
  if (конец !== undefined && конец.reason === 'asleep') return { at: конец.at, точно: true }
  if (конец !== undefined && (конец.reason === 'awake' || конец.reason === 'reset')) return { at: конец.at, точно: false }
  const план = п.ночное ? НОЧНОЕ_БЕЗ_ОТВЕТА_МИН : totalMinutes(program)
  return { at: п.at + план * МИН, точно: false }
}

export interface SleepSpan {
  /** Начало сна: момент «уснул», точный или расчётный. Ключ строки. */
  from: number
  /** Следующее «Проснулся». */
  to: number
  /** Длительность; null — промежуток длиннее 30 часов, это не сон, а брошенное приложение. */
  minutes: number | null
  /** Начало не подтверждено ответом «уснул»: по плану окна или по моменту «нет»/сброса. */
  approx: boolean
  /** Ночной: тянется через начало дня или начался после укладывания; после ночного пробуждения — тоже ночь. */
  night: boolean
  /** Пробуждение перед сном: ключ окна (для правки и комментария) или ночного пробуждения. */
  windowAt: number
  afterNightWake: boolean
  /** Где уснул и комментарий — из записи о конце пробуждения. */
  place?: string
  note?: string
  /** Обычная длительность дневного сна по возрасту, минуты. */
  napNorm: readonly [number, number]
  /** Оценка дневного сна против нормы; у ночного и без данных — none. */
  rating: Rating
  /** Сон идёт прямо сейчас: конца ещё нет, `to` — текущий момент (22.10.2026). */
  ongoing?: boolean
}

/**
 * Конец ночи для сна: ближайшие «пять утра» или начало дня, что раньше. Сон, который
 * через него прошёл, — ночной, во сколько бы его ни уложили.
 *
 * Раньше брали только начало дня: ночь 30.10.2026 у Паши (уложили в 21:09, укладывание в
 * настройках 21:30, подъём в 06:05 при начале дня в 7:00) не началась «ночью» и кончилась
 * до начала дня — и девять часов записались дневным сном (2.11.2026).
 */
function конецНочи(from: number, settings: DaySettings): number {
  return nextDayStart(from, { ...settings, dayStartMin: Math.min(settings.dayStartMin, NIGHT_END_MIN) })
}

/**
 * Сон как список (решение заказчика 20.10.2026): от «уснул» после каждого пробуждения до
 * следующего «Проснулся». Без ответа «уснул» начало берётся по плану, и сон помечается
 * примерным — он остаётся в списке, а не выпадает: иначе список дырявый. Промежуток
 * длиннее 30 часов — не сон, а забытое приложение: минут нет, в выгрузке «нет данных».
 * Программа берётся на день пробуждения: норма сна меняется с возрастом.
 */
export function sleepSpans(events: DayEvent[], windows: number[], programAt: (at: number) => WakeProgram, settings: DaySettings = DEFAULT_DAY): SleepSpan[] {
  const все = пробуждения(events, windows)
  const out: SleepSpan[] = []
  for (const п of все) {
    const следующее = все.find((x) => x.at > п.at)
    if (следующее === undefined) continue
    const program = programAt(п.at)
    const уснули = началоСна(п, events, program)
    const минут = (следующее.at - уснули.at) / МИН
    if (минут <= 0) continue
    const конец = конецЗаписи(п, events)
    // Ночь: после ночного пробуждения, с укладывания, через начало дня — или сон, который
    // кончился ночным пробуждением: это вечернее укладывание, а не дневной сон
    const night = п.ночное || следующее.ночное || isNight(уснули.at, settings) || следующее.at >= конецНочи(уснули.at, settings)
    const napNorm = sleepNormOf(program).napMinutes
    const minutes = минут > САМЫЙ_ДОЛГИЙ_СОН_МИН ? null : Math.round(минут)
    out.push({
      from: уснули.at,
      to: следующее.at,
      minutes,
      approx: !уснули.точно,
      night,
      windowAt: п.at,
      afterNightWake: п.ночное,
      ...(конец?.place === undefined ? {} : { place: конец.place }),
      ...(конец?.note === undefined ? {} : { note: конец.note }),
      napNorm,
      rating: minutes === null || night ? 'none' : rate(minutes, napNorm),
    })
  }
  return out
}

/** День, к которому относится сон в списках и выгрузке: ночной — к дню, вечером которого начался. */
/**
 * Сон, который идёт прямо сейчас: после ответа «уснул» и до следующего «Проснулся».
 *
 * В списках его не было — а отметить, где уснул, и написать пару слов хочется в моменте,
 * как у идущего окна (просьба заказчика 22.10.2026). Конец — текущий момент; дольше
 * 30 часов — это уже не сон, а забытое приложение, тогда и показывать нечего.
 *
 * Признак — отметка таймера «уснул» ([since]), та же, что ведёт отсчёт сна на его экране.
 * Одной последней записи «уснул» мало: окно, вписанное руками за вчера, тоже кончается
 * ответом «уснул», но малыш от этого сутки спустя не «спит до сих пор».
 */
export function ongoingSleep(
  events: DayEvent[],
  windows: number[],
  programAt: (at: number) => WakeProgram,
  since: number | null,
  settings: DaySettings = DEFAULT_DAY,
  now = Date.now(),
): SleepSpan | null {
  if (since === null) return null
  const п = пробуждения(events, windows).at(-1)
  if (п === undefined) return null
  const конец = конецЗаписи(п, events)
  if (конец === undefined || конец.reason !== 'asleep') return null
  const минут = (now - конец.at) / МИН
  if (минут < 0 || минут > САМЫЙ_ДОЛГИЙ_СОН_МИН) return null
  const program = programAt(п.at)
  const night = п.ночное || isNight(конец.at, settings) || now >= конецНочи(конец.at, settings)
  return {
    from: конец.at,
    to: now,
    minutes: Math.round(минут),
    approx: false,
    night,
    windowAt: п.at,
    afterNightWake: п.ночное,
    ...(конец.place === undefined ? {} : { place: конец.place }),
    ...(конец.note === undefined ? {} : { note: конец.note }),
    napNorm: sleepNormOf(program).napMinutes,
    rating: 'none',
    ongoing: true,
  }
}

export function sleepDayOf(s: SleepSpan, settings: DaySettings = DEFAULT_DAY): number {
  return s.night ? epochDayOf(nextDayStart(s.from, settings) - DAY) : epochDayOf(s.from)
}

/** Границы суток календарного дня [day]: от подъёма до подъёма, как считает сводка. */
export function dayRange(day: number, events: DayEvent[], windows: number[], settings: DaySettings = DEFAULT_DAY): readonly [number, number] {
  const все = пробуждения(events, windows)
  return [началоСуток(day, все, settings), началоСуток(day + 1, все, settings)]
}

/** Сводка за календарный день [day] (epochDay в местном поясе, см. `days.ts`). */
export function dayStats(day: number, events: DayEvent[], windows: number[], program: WakeProgram, settings: DaySettings = DEFAULT_DAY): DayStats {
  const все = пробуждения(events, windows)
  const от = началоСуток(day, все, settings)
  const до = началоСуток(day + 1, все, settings)
  const свои = все.filter((п) => п.at >= от && п.at < до)

  let сон = 0
  let примерно = false
  let посчитано = 0
  for (const п of свои) {
    const следующее = все.find((x) => x.at > п.at)
    if (следующее === undefined) continue // ещё не проснулся — сон не закончился
    const уснули = началоСна(п, events, program)
    const минут = (следующее.at - уснули.at) / МИН
    if (минут <= 0 || минут > САМЫЙ_ДОЛГИЙ_СОН_МИН) continue
    сон += минут
    посчитано += 1
    if (!уснули.точно) примерно = true
  }

  const sleep = sleepNormOf(program)
  const sleepNorm: readonly [number, number] = [sleep.perDayHours[0] * 60, sleep.perDayHours[1] * 60]
  const окон = свои.filter((п) => !п.ночное).length
  const норма = windowsNorm(program)
  return {
    windows: окон,
    windowsNorm: норма,
    windowsRating: окон === 0 ? 'none' : rate(окон, норма),
    sleepMinutes: Math.round(сон),
    sleepNorm,
    sleepRating: посчитано === 0 ? 'none' : rate(сон, sleepNorm),
    sleepApprox: примерно,
    nightWakes: свои.filter((п) => п.ночное).length,
  }
}
